export interface SubjectFrame {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface PublicArtTemplate {
  id: string;
  site_id: string;
  base_media_id: string;
  title: string;
  base_image_url: string;
  base_width: number;
  base_height: number;
  subject_x: number;
  subject_y: number;
  subject_width: number;
  subject_height: number;
  published: boolean;
}

export function normaliseSubjectFrame(frame: SubjectFrame): SubjectFrame {
  const width = Math.min(1, Math.max(0.02, frame.width));
  const height = Math.min(1, Math.max(0.02, frame.height));
  return {
    width,
    height,
    x: Math.min(1 - width, Math.max(0, frame.x)),
    y: Math.min(1 - height, Math.max(0, frame.y)),
  };
}

export function templateSubjectFrame(template: Pick<PublicArtTemplate, 'subject_x' | 'subject_y' | 'subject_width' | 'subject_height'>): SubjectFrame {
  return normaliseSubjectFrame({
    x: Number(template.subject_x),
    y: Number(template.subject_y),
    width: Number(template.subject_width),
    height: Number(template.subject_height),
  });
}

export function loadCanvasImage(source: string | Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.decoding = 'async';
    if (typeof source === 'string') image.crossOrigin = 'anonymous';
    const objectUrl = source instanceof Blob ? URL.createObjectURL(source) : null;
    image.onload = () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      resolve(image);
    };
    image.onerror = () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      reject(new Error('Não foi possível carregar uma das imagens da arte.'));
    };
    image.src = objectUrl ?? (typeof source === 'string' ? source : '');
  });
}

type CanvasRaster = HTMLImageElement | HTMLCanvasElement;

function rasterDimensions(image: CanvasRaster) {
  return image instanceof HTMLImageElement
    ? { width: image.naturalWidth, height: image.naturalHeight }
    : { width: image.width, height: image.height };
}

/** Exige alfa transparente na borda e uma área opaca no centro da imagem. */
export function hasReliableTransparency(image: CanvasRaster): boolean {
  const source = rasterDimensions(image);
  const maxDimension = 320;
  const scale = Math.min(1, maxDimension / Math.max(source.width, source.height));
  const width = Math.max(1, Math.round(source.width * scale));
  const height = Math.max(1, Math.round(source.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) return false;
  context.drawImage(image, 0, 0, width, height);
  const { data } = context.getImageData(0, 0, width, height);
  let transparent = 0;
  let opaque = 0;
  for (let i = 3; i < data.length; i += 4) {
    if (data[i] <= 8) transparent += 1;
    if (data[i] >= 245) opaque += 1;
  }
  const pixels = width * height;
  return transparent > pixels * 0.015 && opaque > pixels * 0.01;
}

/**
 * Alguns modelos de imagem não preservam o canal alfa. Nestes casos a Edge
 * Function solicita um fundo verde #00FF00, que transformamos em alfa antes
 * da composição. A verificação de quantidade impede que uma foto normal seja
 * tratada acidentalmente como chroma key.
 */
function removeChromaGreenBackground(image: HTMLImageElement): HTMLCanvasElement | null {
  const source = rasterDimensions(image);
  const maxDimension = 2200;
  const scale = Math.min(1, maxDimension / Math.max(source.width, source.height));
  const width = Math.max(1, Math.round(source.width * scale));
  const height = Math.max(1, Math.round(source.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) return null;

  context.drawImage(image, 0, 0, width, height);
  const pixels = context.getImageData(0, 0, width, height);
  let chromaPixels = 0;
  for (let index = 0; index < pixels.data.length; index += 4) {
    const red = pixels.data[index];
    const green = pixels.data[index + 1];
    const blue = pixels.data[index + 2];
    const greenDominance = green - Math.max(red, blue);
    const isChroma = green >= 135 && greenDominance >= 70 && red <= 150 && blue <= 150;
    if (!isChroma) continue;
    chromaPixels += 1;
    // Mantem uma pequena suavização para não recortar os contornos da pessoa.
    pixels.data[index + 3] = greenDominance < 105 ? 64 : 0;
  }

  if (chromaPixels < width * height * 0.012) return null;
  context.putImageData(pixels, 0, 0);
  return canvas;
}

function colorDistance(red: number, green: number, blue: number, sample: [number, number, number]) {
  const redDelta = red - sample[0];
  const greenDelta = green - sample[1];
  const blueDelta = blue - sample[2];
  return Math.sqrt(redDelta * redDelta + greenDelta * greenDelta + blueDelta * blueDelta);
}

/**
 * Fallback para respostas opacas do Gemini com fundo uniforme. Descobre a cor
 * dominante nas bordas e remove apenas a região conectada às bordas. Não é uma
 * segmentação genérica: se o fundo não for claramente uniforme, não faz nada.
 */
function removeUniformEdgeBackground(image: HTMLImageElement): HTMLCanvasElement | null {
  const source = rasterDimensions(image);
  const maxDimension = 1800;
  const scale = Math.min(1, maxDimension / Math.max(source.width, source.height));
  const width = Math.max(1, Math.round(source.width * scale));
  const height = Math.max(1, Math.round(source.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) return null;

  context.drawImage(image, 0, 0, width, height);
  const imageData = context.getImageData(0, 0, width, height);
  const { data } = imageData;
  const edgeIndexes: number[] = [];
  for (let x = 0; x < width; x += 1) {
    edgeIndexes.push(x, (height - 1) * width + x);
  }
  for (let y = 1; y < height - 1; y += 1) {
    edgeIndexes.push(y * width, y * width + width - 1);
  }

  const clusters = new Map<string, { count: number; red: number; green: number; blue: number }>();
  for (const pixel of edgeIndexes) {
    const offset = pixel * 4;
    const red = data[offset];
    const green = data[offset + 1];
    const blue = data[offset + 2];
    const key = `${Math.floor(red / 24)}:${Math.floor(green / 24)}:${Math.floor(blue / 24)}`;
    const cluster = clusters.get(key) ?? { count: 0, red: 0, green: 0, blue: 0 };
    cluster.count += 1;
    cluster.red += red;
    cluster.green += green;
    cluster.blue += blue;
    clusters.set(key, cluster);
  }
  const backgroundCluster = [...clusters.values()].sort((first, second) => second.count - first.count)[0];
  if (!backgroundCluster || backgroundCluster.count < edgeIndexes.length * 0.42) return null;
  const background: [number, number, number] = [
    backgroundCluster.red / backgroundCluster.count,
    backgroundCluster.green / backgroundCluster.count,
    backgroundCluster.blue / backgroundCluster.count,
  ];

  const visited = new Uint8Array(width * height);
  const queue: number[] = [];
  const enqueue = (pixel: number) => {
    if (visited[pixel]) return;
    const offset = pixel * 4;
    if (colorDistance(data[offset], data[offset + 1], data[offset + 2], background) > 64) return;
    visited[pixel] = 1;
    queue.push(pixel);
  };
  edgeIndexes.forEach(enqueue);
  for (let cursor = 0; cursor < queue.length; cursor += 1) {
    const pixel = queue[cursor];
    const x = pixel % width;
    const y = Math.floor(pixel / width);
    if (x > 0) enqueue(pixel - 1);
    if (x < width - 1) enqueue(pixel + 1);
    if (y > 0) enqueue(pixel - width);
    if (y < height - 1) enqueue(pixel + width);
  }

  if (queue.length < width * height * 0.025 || queue.length > width * height * 0.94) return null;
  for (const pixel of queue) data[pixel * 4 + 3] = 0;
  context.putImageData(imageData, 0, 0);
  return canvas;
}

export async function composePublicArt(template: PublicArtTemplate, subjectBlob: Blob): Promise<Blob> {
  const [base, subject] = await Promise.all([loadCanvasImage(template.base_image_url), loadCanvasImage(subjectBlob)]);
  const preparedSubject = hasReliableTransparency(subject)
    ? subject
    : removeChromaGreenBackground(subject) ?? removeUniformEdgeBackground(subject);
  if (!preparedSubject || !hasReliableTransparency(preparedSubject)) {
    throw new Error('A remoção de fundo não retornou transparência confiável. Tente outra foto com a pessoa bem destacada do fundo.');
  }

  const canvas = document.createElement('canvas');
  canvas.width = template.base_width;
  canvas.height = template.base_height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Seu navegador não consegue compor esta arte.');

  context.drawImage(base, 0, 0, canvas.width, canvas.height);
  const frame = templateSubjectFrame(template);
  const targetWidth = frame.width * canvas.width;
  const targetHeight = frame.height * canvas.height;
  const subjectSize = rasterDimensions(preparedSubject);
  const scale = Math.min(targetWidth / subjectSize.width, targetHeight / subjectSize.height);
  const width = subjectSize.width * scale;
  const height = subjectSize.height * scale;
  const x = frame.x * canvas.width + (targetWidth - width) / 2;
  const y = frame.y * canvas.height + (targetHeight - height) / 2;
  context.drawImage(preparedSubject, x, y, width, height);

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Não foi possível gerar o PNG final.'))), 'image/png');
  });
}
